import { useEffect, useState } from "react";
import api from "@/lib/api";
import DataTable from "@/components/admin/DataTable";
import SearchBox from "@/components/admin/SearchBox";
import EmptyState from "@/components/admin/EmptyState";
import StatusBadge from "@/components/admin/StatusBadge";
import { MessageSquare } from "lucide-react";
import AdminSupportDetail from "./AdminSupportDetail";

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-AU");
}

function tone(s) {
  if (s === "new") return "amber";
  if (s === "open") return "green";
  if (s === "pending_customer") return "amber";
  if (s === "resolved") return "green";
  if (s === "closed") return "grey";
  return "grey";
}

function actorTone(k) {
  if (k === "admin") return "Admin replied";
  if (k === "customer") return "Customer waiting";
  return "—";
}

const STATUSES = [
  { v: "all", l: "All" },
  { v: "new", l: "New" },
  { v: "open", l: "Open" },
  { v: "pending_customer", l: "Pending customer" },
  { v: "resolved", l: "Resolved" },
  { v: "closed", l: "Closed" },
];

const COLS = [
  { key: "subject", label: "Subject", render: (r) => (
    <div className="min-w-0">
      <div className="truncate text-sm text-brand-ink">{r.subject}</div>
      <div className="text-xs text-brand-muted font-mono truncate">{r.email}</div>
    </div>
  )},
  { key: "project_title", label: "Project", render: (r) => (
    r.project_title
      ? <span className="text-xs text-brand-ink truncate">{r.project_title}</span>
      : <span className="text-xs text-brand-muted">—</span>
  )},
  { key: "status", label: "Status", render: (r) => (
    <StatusBadge tone={tone(r.status)} label={r.status} />
  )},
  { key: "last_activity_actor_kind", label: "Last updated by", render: (r) => (
    <span className={`text-xs ${r.last_activity_actor_kind === "admin" ? "text-brand-teal" : "text-amber-700"}`}>
      {actorTone(r.last_activity_actor_kind)}
    </span>
  )},
  { key: "last_activity_at", label: "Last updated", render: (r) => relTime(r.last_activity_at) },
  { key: "created_at", label: "Created", render: (r) => relTime(r.created_at) },
];

export default function AdminSupport() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (selectedId) return;
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page), page_size: String(pageSize), status,
    });
    if (q) params.set("q", q);
    api.get(`/admin/support?${params.toString()}`)
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [page, pageSize, q, status, selectedId, reloadKey]);

  if (selectedId) {
    return (
      <AdminSupportDetail
        ticketId={selectedId}
        onBack={() => { setSelectedId(null); setReloadKey((k) => k + 1); }}
      />
    );
  }

  return (
    <div data-testid="admin-support">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif text-3xl text-brand-ink tracking-tight">Support</h2>
          <div className="text-xs text-brand-muted mt-1">
            {data.total} {status === "all" ? "total" : status} ticket{data.total === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBox
          value={q}
          onChange={(v) => { setQ(v); setPage(1); }}
          placeholder="Search subject, name or email…"
          testId="support-search"
        />
        <div className="inline-flex gap-px bg-brand-linen border border-brand-linen" data-testid="support-status-pills">
          {STATUSES.map((s) => (
            <button
              key={s.v}
              data-testid={`support-status-${s.v}`}
              onClick={() => { setStatus(s.v); setPage(1); }}
              className={`px-3 py-1.5 text-xs ${
                status === s.v ? "bg-brand-navy text-white" : "bg-white hover:bg-brand-linen/30"
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </div>

      {err && (
        <div className="text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3 mb-4">{err}</div>
      )}

      <DataTable
        testId="support-table"
        columns={COLS}
        rows={data.items}
        total={data.total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        onRowClick={(row) => setSelectedId(row.id)}
        loading={loading}
        empty={(
          <EmptyState
            icon={MessageSquare}
            title={`No ${status === "all" ? "" : status} tickets`}
            caption="Try a different filter or clear the search."
            testId="support-empty"
          />
        )}
      />
    </div>
  );
}
